function readPath(source, path) {
  return path.split(".").reduce((current, key) => {
    if (current == null) return undefined;
    return current[key];
  }, source);
}

function deepFindFirst(source, matcher) {
  if (source == null) return undefined;
  if (Array.isArray(source)) {
    for (const item of source) {
      const found = deepFindFirst(item, matcher);
      if (found !== undefined) return found;
    }
    return undefined;
  }
  if (typeof source === "object") {
    for (const [key, value] of Object.entries(source)) {
      if (matcher(key, value)) return value;
      const found = deepFindFirst(value, matcher);
      if (found !== undefined) return found;
    }
  }
  return undefined;
}

function findFirstArray(payload, candidatePaths = [], keyHints = []) {
  for (const path of candidatePaths) {
    const value = readPath(payload, path);
    if (Array.isArray(value)) return value;
  }
  return (
    deepFindFirst(payload, (key, value) => {
      if (!Array.isArray(value)) return false;
      const normalizedKey = String(key || "").toLowerCase();
      return keyHints.some((hint) => normalizedKey.includes(hint));
    }) || []
  );
}

function findFirstNumber(payload, candidatePaths = [], keyHints = []) {
  for (const path of candidatePaths) {
    const value = readPath(payload, path);
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  const fallback = deepFindFirst(payload, (key, value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return false;
    const normalizedKey = String(key || "").toLowerCase();
    return keyHints.some((hint) => normalizedKey.includes(hint));
  });
  const parsedFallback = Number(fallback);
  return Number.isFinite(parsedFallback) ? parsedFallback : 0;
}

function firstNonEmpty(source = {}, candidates = []) {
  return candidates
    .map((candidate) => source?.[candidate])
    .find((value) => String(value || "").trim().length > 0);
}

function firstNumeric(source = {}, candidates = []) {
  const found = candidates
    .map((candidate) => Number(source?.[candidate]))
    .find((parsed) => Number.isFinite(parsed));
  return Number.isFinite(found) ? found : null;
}

function firstNumericOrZero(source = {}, candidates = []) {
  const found = firstNumeric(source, candidates);
  return Number.isFinite(found) ? found : 0;
}

function sumGroupedMetric(payload = {}, candidates = []) {
  const producaoAgrupada = findFirstArray(payload, ["producaoAgrupada"], ["producaoagrupada"]);
  if (!Array.isArray(producaoAgrupada) || !producaoAgrupada.length) return 0;

  return producaoAgrupada.reduce((bankTotal, banco) => {
    const convenios = Array.isArray(banco?.convenios) ? banco.convenios : [];

    return (
      bankTotal +
      convenios.reduce((convenioTotal, convenio) => {
        const tipos = Array.isArray(convenio?.tipos) ? convenio.tipos : [];

        return (
          convenioTotal +
          tipos.reduce((tipoTotal, tipo) => {
            const value = firstNumeric(tipo, candidates);
            return tipoTotal + (Number.isFinite(value) ? value : 0);
          }, 0)
        );
      }, 0)
    );
  }, 0);
}

function normalizeIsoDate(rawValue) {
  const raw = String(rawValue || "").trim();
  if (!raw) return "";
  return raw.includes("T") ? raw.slice(0, 10) : raw;
}

export function normalizeDashboardResumo(payload = {}) {
  const producaoBrutaTotal = findFirstNumber(payload, ["resumo.producaoBrutaTotal", "resumoPeriodo.producaoBrutaTotal", "producaoBrutaTotal"], ["producaobruta"]);
  const producaoLiquidaTotal = findFirstNumber(payload, ["resumo.producaoLiquidaTotal", "resumoPeriodo.producaoLiquidaTotal", "producaoLiquidaTotal"], ["producaoliquida"]);
  const producaoComissionadaTotal = findFirstNumber(
    payload,
    ["resumo.producaoComissionadaTotal", "resumoPeriodo.producaoComissionadaTotal", "producaoComissionadaTotal"],
    ["producaocomissionada", "producaoremunerada"]
  );
  const producaoBrutaAgrupada = sumGroupedMetric(payload, ["producaoBrutaTotal", "ProducaoBrutaTotal", "producaoBruta", "ProducaoBruta"]);
  const producaoLiquidaAgrupada = sumGroupedMetric(payload, ["producaoLiquidaTotal", "ProducaoLiquidaTotal", "producaoLiquida", "ProducaoLiquida"]);
  const producaoComissionadaAgrupada = sumGroupedMetric(payload, [
    "producaoComissionadaTotal",
    "ProducaoComissionadaTotal",
    "producaoComissionada",
    "ProducaoComissionada",
    "producaoRemuneradaTotal",
    "ProducaoRemuneradaTotal",
    "producaoRemunerada",
    "ProducaoRemunerada",
  ]);

  return {
    producaoBrutaTotal: producaoBrutaTotal > 0 ? producaoBrutaTotal : producaoBrutaAgrupada,
    producaoLiquidaTotal: producaoLiquidaTotal > 0 ? producaoLiquidaTotal : producaoLiquidaAgrupada,
    producaoComissionadaTotal: producaoComissionadaTotal > 0 ? producaoComissionadaTotal : producaoComissionadaAgrupada,
    percentualRepasseMedio: findFirstNumber(
      payload,
      ["resumo.percentRepasseMedio", "resumo.percentualRepasseMedio", "resumoPeriodo.percentualRepasseMedio", "percentRepasseMedio", "percentualRepasseMedio"],
      ["repassemedio"]
    ),
    spreadMedio: findFirstNumber(payload, ["resumo.spreadMedio", "resumoPeriodo.spreadMedio", "spreadMedio"], ["spreadmedio"]),
  };
}

export function normalizeDashboardContractStatus(payload = {}) {
  return {
    aprovados: findFirstNumber(payload, ["statusContratos.aprovados", "resumo.statusContratos.aprovados"], ["aprovad"]),
    cancelados: findFirstNumber(payload, ["statusContratos.cancelados", "resumo.statusContratos.cancelados"], ["cancelad"]),
    negados: findFirstNumber(payload, ["statusContratos.negados", "resumo.statusContratos.negados"], ["negad"]),
    emAndamento: findFirstNumber(payload, ["statusContratos.emAndamento", "resumo.statusContratos.emAndamento"], ["emandamento", "andamento"]),
  };
}

export function normalizeDashboardEvolucao(payload = {}) {
  const points = findFirstArray(payload, ["evolucaoNoTempo.pontos", "evolucao.pontos"], ["evolucao", "pontos"]);
  return points
    .map((point) => ({
      data: normalizeIsoDate(point.data || point.Data || point.date || point.Date || point.periodo || point.Periodo || ""),
      producaoBruta: firstNumericOrZero(point, [
        "producaoBruta",
        "ProducaoBruta",
        "producaoBrutaTotal",
        "ProducaoBrutaTotal",
        "producaoTotal",
        "ProducaoTotal",
        "valorBruto",
        "ValorBruto",
      ]),
      producaoLiquida: firstNumericOrZero(point, [
        "producaoLiquida",
        "ProducaoLiquida",
        "producaoLiquidaTotal",
        "ProducaoLiquidaTotal",
        "valorLiquido",
        "ValorLiquido",
      ]),
      producaoComissionada: firstNumericOrZero(point, [
        "producaoComissionada",
        "ProducaoComissionada",
        "producaoComissionadaTotal",
        "ProducaoComissionadaTotal",
        "producaoRemunerada",
        "ProducaoRemunerada",
        "producaoRemuneradaTotal",
        "ProducaoRemuneradaTotal",
        "valorComissionado",
        "ValorComissionado",
      ]),
      contratosAprovados: firstNumericOrZero(point, ["contratosAprovados", "ContratosAprovados", "aprovados", "Aprovados"]),
      contratosCancelados: firstNumericOrZero(point, ["contratosCancelados", "ContratosCancelados", "cancelados", "Cancelados"]),
      contratosNegados: firstNumericOrZero(point, ["contratosNegados", "ContratosNegados", "negados", "Negados"]),
      contratosEmAndamento: firstNumericOrZero(
        point,
        ["contratosEmAndamento", "ContratosEmAndamento", "emAndamento", "EmAndamento", "andamento", "Andamento"]
      ),
    }))
    .filter((point) => point.data);
}

function normalizeRankingItem(item = {}, nameCandidates = [], valueCandidates = []) {
  const name = firstNonEmpty(item, nameCandidates);
  const value = valueCandidates
    .map((candidate) => Number(item?.[candidate]))
    .find((parsed) => Number.isFinite(parsed));

  return {
    label: String(name || "").trim(),
    value: Number.isFinite(value) ? value : 0,
  };
}

export function normalizeRankingParceiros(payload = {}) {
  const raw = findFirstArray(payload, ["rankingParceiros", "rankingParceiro", "topParceiros"], ["parceiro"]);
  return raw
    .map((item) => {
      const normalized = normalizeRankingItem(
        item,
        ["parceiroNome", "ParceiroNome", "nomeParceiro", "NomeParceiro", "nome", "Nome", "label", "Label"],
        ["producaoBrutaTotal", "ProducaoBrutaTotal", "producaoTotal", "ProducaoTotal", "valor", "Valor", "total", "Total", "quantidade", "Quantidade", "qtd", "Qtd", "totalContratos", "TotalContratos"]
      );
      const producaoBrutaTotal = firstNumeric(item, [
        "producaoBrutaTotal",
        "ProducaoBrutaTotal",
        "producaoTotal",
        "ProducaoTotal",
        "valor",
        "Valor",
        "total",
        "Total",
      ]);
      const producaoLiquidaTotal = firstNumeric(item, [
        "producaoLiquidaTotal",
        "ProducaoLiquidaTotal",
        "producaoLiquida",
        "ProducaoLiquida",
        "valorLiquido",
        "ValorLiquido",
        "totalLiquido",
        "TotalLiquido",
      ]);
      const producaoComissionadaTotal = firstNumeric(item, [
        "producaoComissionadaTotal",
        "ProducaoComissionadaTotal",
        "producaoComissionada",
        "ProducaoComissionada",
        "valorComissionado",
        "ValorComissionado",
        "producaoRemuneradaTotal",
        "ProducaoRemuneradaTotal",
        "producaoRemunerada",
        "ProducaoRemunerada",
      ]);
      const quantidade = firstNumeric(item, [
        "quantidadeContratos",
        "QuantidadeContratos",
        "qtdContratos",
        "QtdContratos",
        "quantidade",
        "Quantidade",
        "qtd",
        "Qtd",
        "totalContratos",
        "TotalContratos",
        "contratosAprovados",
        "ContratosAprovados",
        "contratos",
        "Contratos",
      ]);

      return {
        ...normalized,
        producaoBrutaTotal: Number.isFinite(producaoBrutaTotal) ? producaoBrutaTotal : normalized.value,
        producaoLiquidaTotal: Number.isFinite(producaoLiquidaTotal) ? producaoLiquidaTotal : 0,
        producaoComissionadaTotal: Number.isFinite(producaoComissionadaTotal) ? producaoComissionadaTotal : 0,
        quantidadeContratos: Number.isFinite(quantidade) ? quantidade : 0,
        quantidade: Number.isFinite(quantidade) ? quantidade : 0,
        supervisorId: firstNumeric(item, ["supervisorId", "SupervisorId", "idSupervisor", "IdSupervisor", "responsavelId", "ResponsavelId", "usuarioId", "UsuarioId"]),
        idJoin: firstNumeric(item, ["idJoin", "IdJoin", "parceiroIdJoin", "ParceiroIdJoin", "joinId", "JoinId", "id_join", "parceiroId", "ParceiroId", "partnerId", "PartnerId", "idParceiro", "IdParceiro", "parceiro_id"]),
      };
    })
    .filter((item) => item.label)
    .sort((left, right) => right.value - left.value);
}

function getRankingMetricValue(item, mode) {
  switch (mode) {
    case "LIQUIDO":
      return Number(item?.producaoLiquidaTotal || 0);
    case "COMISSIONADO":
      return Number(item?.producaoComissionadaTotal || 0);
    case "CONTRATOS":
      return Number(item?.quantidadeContratosTotal || item?.quantidadeContratos || item?.quantidade || item?.value || 0);
    case "BRUTO":
    default:
      return Number(item?.producaoBrutaTotal || item?.value || 0);
  }
}

function normalizeRankingAggregateItem(item, nameCandidates = []) {
  const base = normalizeRankingItem(
    item,
    nameCandidates,
    ["producaoBrutaTotal", "ProducaoBrutaTotal", "producaoTotal", "ProducaoTotal", "valor", "Valor", "total", "Total"]
  );

  const producaoBrutaTotal = firstNumeric(item, ["producaoBrutaTotal", "ProducaoBrutaTotal", "producaoTotal", "ProducaoTotal", "valor", "Valor", "total", "Total"]);
  const producaoLiquidaTotal = firstNumeric(item, ["producaoLiquidaTotal", "ProducaoLiquidaTotal", "producaoLiquida", "ProducaoLiquida", "valorLiquido", "ValorLiquido", "totalLiquido", "TotalLiquido"]);
  const producaoComissionadaTotal = firstNumeric(item, [
    "producaoComissionadaTotal",
    "ProducaoComissionadaTotal",
    "producaoComissionada",
    "ProducaoComissionada",
    "producaoRemuneradaTotal",
    "ProducaoRemuneradaTotal",
    "producaoRemunerada",
    "ProducaoRemunerada",
    "valorComissionado",
    "ValorComissionado",
  ]);
  const quantidadeContratos = firstNumeric(item, [
    "quantidadeContratosTotal",
    "QuantidadeContratosTotal",
    "qtdContratosTotal",
    "QtdContratosTotal",
    "quantidadeContratos",
    "QuantidadeContratos",
    "qtdContratos",
    "QtdContratos",
    "quantidade",
    "Quantidade",
    "qtd",
    "Qtd",
    "totalContratos",
    "TotalContratos",
    "contratosTotal",
    "ContratosTotal",
  ]);

  return {
    ...base,
    producaoBrutaTotal: Number.isFinite(producaoBrutaTotal) ? producaoBrutaTotal : base.value,
    producaoLiquidaTotal: Number.isFinite(producaoLiquidaTotal) ? producaoLiquidaTotal : 0,
    producaoComissionadaTotal: Number.isFinite(producaoComissionadaTotal) ? producaoComissionadaTotal : 0,
    quantidadeContratos: Number.isFinite(quantidadeContratos) ? quantidadeContratos : 0,
    quantidade: Number.isFinite(quantidadeContratos) ? quantidadeContratos : 0,
  };
}

export function normalizeRankingBancos(payload = {}, mode = "BRUTO") {
  const raw = findFirstArray(payload, ["rankingBancos", "producaoPorBanco", "topBancos", "producaoAgrupada"], ["banco", "producaoagrupada"]);
  return raw
    .map((item) => {
      const normalized = normalizeRankingAggregateItem(item, ["bancoNome", "BancoNome", "nomeBanco", "NomeBanco", "nome", "Nome", "label", "Label"]);
      
      // Se não vier agregado da API, soma a partir dos convênios/tipos
      if (!normalized.producaoBrutaTotal && Array.isArray(item.convenios)) {
        normalized.producaoBrutaTotal = item.convenios.reduce((acc, conv) => acc + (Array.isArray(conv.tipos) ? conv.tipos.reduce((accT, tipo) => accT + firstNumericOrZero(tipo, ["producaoBrutaTotal", "ProducaoBrutaTotal", "producaoTotal"]), 0) : 0), 0);
        normalized.producaoLiquidaTotal = item.convenios.reduce((acc, conv) => acc + (Array.isArray(conv.tipos) ? conv.tipos.reduce((accT, tipo) => accT + firstNumericOrZero(tipo, ["producaoLiquidaTotal", "ProducaoLiquidaTotal", "producaoLiquida"]), 0) : 0), 0);
        normalized.producaoComissionadaTotal = item.convenios.reduce((acc, conv) => acc + (Array.isArray(conv.tipos) ? conv.tipos.reduce((accT, tipo) => accT + firstNumericOrZero(tipo, ["producaoComissionadaTotal", "ProducaoComissionadaTotal", "producaoComissionada"]), 0) : 0), 0);
        normalized.quantidadeContratos = item.convenios.reduce((acc, conv) => acc + (Array.isArray(conv.tipos) ? conv.tipos.reduce((accT, tipo) => accT + firstNumericOrZero(tipo, ["quantidadeContratos", "QuantidadeContratos", "qtdContratos", "quantidade"]), 0) : 0), 0);
      }
      return normalized;
    })
    .filter((item) => item.label)
    .map((item) => ({ ...item, value: getRankingMetricValue(item, mode) }))
    .sort((left, right) => Number(right.value || 0) - Number(left.value || 0));
}

export function normalizeRankingConvenios(payload = {}, mode = "BRUTO") {
  let raw = findFirstArray(payload, ["rankingConvenios", "producaoPorConvenio", "topConvenios"], ["convenio"]);
  
  if (!raw || !raw.length) {
    const producaoAgrupada = findFirstArray(payload, ["producaoAgrupada"], ["producaoagrupada"]);
    if (Array.isArray(producaoAgrupada)) {
      const conveniosMap = new Map();
      producaoAgrupada.forEach((banco) => {
        if (!Array.isArray(banco.convenios)) return;
        banco.convenios.forEach((conv) => {
          const nome = firstNonEmpty(conv, ["convenioNome", "ConvenioNome", "nomeConvenio", "NomeConvenio", "nome", "Nome", "label", "Label"]);
          if (!nome) return;
          
          let producaoBrutaTotal = firstNumericOrZero(conv, ["producaoBrutaTotal", "ProducaoBrutaTotal", "producaoTotal"]);
          let producaoLiquidaTotal = firstNumericOrZero(conv, ["producaoLiquidaTotal", "ProducaoLiquidaTotal", "producaoLiquida"]);
          let producaoComissionadaTotal = firstNumericOrZero(conv, ["producaoComissionadaTotal", "ProducaoComissionadaTotal", "producaoComissionada"]);
          let quantidadeContratos = firstNumericOrZero(conv, ["quantidadeContratos", "QuantidadeContratos", "qtdContratos", "quantidade"]);

          if (!producaoBrutaTotal && Array.isArray(conv.tipos)) {
            producaoBrutaTotal = conv.tipos.reduce((acc, tipo) => acc + firstNumericOrZero(tipo, ["producaoBrutaTotal", "ProducaoBrutaTotal", "producaoTotal"]), 0);
            producaoLiquidaTotal = conv.tipos.reduce((acc, tipo) => acc + firstNumericOrZero(tipo, ["producaoLiquidaTotal", "ProducaoLiquidaTotal", "producaoLiquida"]), 0);
            producaoComissionadaTotal = conv.tipos.reduce((acc, tipo) => acc + firstNumericOrZero(tipo, ["producaoComissionadaTotal", "ProducaoComissionadaTotal", "producaoComissionada"]), 0);
            quantidadeContratos = conv.tipos.reduce((acc, tipo) => acc + firstNumericOrZero(tipo, ["quantidadeContratos", "QuantidadeContratos", "qtdContratos", "quantidade"]), 0);
          }

          if (conveniosMap.has(nome)) {
            const existing = conveniosMap.get(nome);
            existing.producaoBrutaTotal += producaoBrutaTotal;
            existing.producaoLiquidaTotal += producaoLiquidaTotal;
            existing.producaoComissionadaTotal += producaoComissionadaTotal;
            existing.quantidadeContratos += quantidadeContratos;
          } else {
            conveniosMap.set(nome, {
              label: nome,
              producaoBrutaTotal,
              producaoLiquidaTotal,
              producaoComissionadaTotal,
              quantidadeContratos
            });
          }
        });
      });
      raw = Array.from(conveniosMap.values());
    }
  }

  return raw
    .map((item) =>
      normalizeRankingAggregateItem(item, ["convenioNome", "ConvenioNome", "nomeConvenio", "NomeConvenio", "nome", "Nome", "label", "Label"])
    )
    .filter((item) => item.label)
    .map((item) => ({ ...item, value: getRankingMetricValue(item, mode) }))
    .sort((left, right) => Number(right.value || 0) - Number(left.value || 0));
}
